import type {PageContent} from '@gravity-ui/page-constructor';

import {Theme, Lang} from '~shared/models/settings';

import {getBackgroundUrl} from './utils';

import {SIGN_UP_GOAL, SIGN_IN_GOAL, WIKI_ROOT_URL} from './constants';

const getEnPromoPageConfig = (theme: Theme) => {
    return {
        background: {
            image: {
                src: getBackgroundUrl(theme),
                disableCompress: true,
            },
        },
        blocks: [
            {
                type: 'header-block',
                width: 'm',
                offset: 'default',
                title: 'Yandex Wiki',
                description:
                    'A free service for creating a corporate knowledge hub. \n\nCreate wiki pages, add content, and edit them along with your colleagues.',
                buttons: [
                    {
                        text: 'Log in to Wiki',
                        primary: true,
                        theme: 'accent',
                        size: 'promo',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_IN_GOAL],
                    },
                    {
                        text: 'Try for free',
                        primary: true,
                        theme: 'pseudo',
                        size: 'promo',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_UP_GOAL],
                    },
                ],
            },
            {
                type: 'extended-features-block',
                items: [
                    {
                        title: 'Create a knowledge base',
                        text: 'Keep project descriptions, instructions, and other information in Wiki. Organize hierarchies of nested pages and link them with cross-references.',
                    },
                    {
                        title: 'Save time searching for information',
                        text: 'Search all the pages of your knowledge base and quickly find the answers you need.',
                    },
                    {
                        title: 'Design your pages',
                        text: "Format texts and add drawings, tables and links using Markdown. Use headings and cuts to draw attention to what's most important.",
                    },
                    {
                        title: 'Manage access',
                        text: 'Grant access to all your employees, restrict the editing of specific pages, or prohibit the viewing of confidential information.',
                    },
                    {
                        title: 'Track changes',
                        text: 'All page edits are saved in the changelog, and you are notified of any changes to pages you subscribe to.',
                    },
                    {
                        title: 'Add tasks and forms',
                        text: 'Add tasks lists from Yandex Tracker — task names and assignees update automatically. Embed request and survey forms from Yandex Forms.',
                        link: {
                            theme: 'normal',
                            arrow: true,
                            text: 'Yandex Tracker',
                            url: 'https://cloud.yandex.com/services/tracker',
                        },
                    },
                ],
            },
            {
                type: 'media-block',
                direction: 'media-content',
                title: 'Keep your project plan on Yandex Wiki',
                description:
                    'Store a full project description and add task lists from Yandex Tracker.',
                media: {
                    image: {
                        src: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/wiki/wiki-1-en.png',
                        disableCompress: true,
                    },
                },
                largeMedia: true,
            },
            {
                type: 'slider-block',
                anchor: {
                    url: 'solutions',
                    text: 'Solutions',
                },
                title: {
                    text: 'What else can Yandex Wiki do?',
                },
                children: [
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_18_2021.png',
                            title: 'In-house knowledge base',
                        },
                        text: 'Build a knowledge base for your entire team and collaborate with your colleagues to fill it with everything you need.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_14.png',
                            title: 'Corporate portal',
                        },
                        text: 'Create an easy-to-use company portal for your employees. Bring together important information, links, contacts, and request forms.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_5.png',
                            title: 'Instructions for work processes',
                        },
                        text: 'Keep instructions for internal processes on Yandex Wiki. Embed Yandex.Forms to create tasks in Yandex Tracker directly from the Yandex Wiki page.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_17_2021.png',
                            title: 'Keep personal notes',
                        },
                        text: 'Save drafts, plans, and meeting notes to Yandex Wiki.',
                    },
                ],
            },
            {
                type: 'questions-block',
                title: 'FAQ',
                anchor: {
                    text: 'FAQ',
                    url: 'questions',
                },
                items: [
                    {
                        title: 'How do I start using Yandex Wiki?',
                        text: "Yandex Wiki is a free service that allows you to build your own knowledge base. To use Yandex Wiki, just log in to your account:\n* If you are a Yandex Cloud Organization user, log in to your work account. Yandex Wiki will be activated for your organization and you'll be able to create and edit pages right away.\n* If you are not a Yandex Cloud Organization user yet, log in to your Yandex account. A new organization will be created automatically for you and you can start using Yandex Wiki.",
                    },
                    {
                        title: 'How can I integrate Yandex Tracker and Wiki?',
                        text: "Simply add Yandex Tracker to your organization. You'll be able to add links to Tracker tasks into Wiki pages and dynamic tables and see their names, as well as open task cards from the Yandex Wiki interface.",
                        link: {
                            url: 'https://cloud.yandex.com/services/tracker',
                            text: 'Go to Yandex Tracker',
                            theme: 'normal',
                            arrow: true,
                        },
                    },
                    {
                        title: 'How can I integrate Yandex Wiki and Forms?',
                        text: 'To embed a form on a page, use the embed code on Yandex Wiki. \n\nTo save form responses on a page, go to the Integration tab in Yandex Forms and select Yandex Wiki. For detailed instructions, click the link below.',
                        link: {
                            url: 'https://cloud.yandex.com/services/forms',
                            text: 'Go to Yandex Forms',
                            theme: 'normal',
                            arrow: true,
                        },
                    },
                ],
            },
            {
                type: 'info-block',
                title: 'Start using Yandex Wiki',
                buttons: [
                    {
                        text: 'Log in to Wiki',
                        theme: 'raised',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_IN_GOAL],
                    },
                ],
                sectionsTitle: 'Useful links',
                links: [
                    {
                        text: 'Documentation',
                        url: 'https://cloud.yandex.com/docs/wiki',
                    },
                    {
                        text: 'Pricing',
                        url: 'https://cloud.yandex.com/docs/wiki/pricing',
                    },
                ],
            },
        ],
    } as PageContent;
};

const getRuPromoPageConfig = (theme: Theme) => {
    return {
        background: {
            image: {
                src: getBackgroundUrl(theme),
                disableCompress: true,
            },
        },
        blocks: [
            {
                type: 'header-block',
                width: 'm',
                offset: 'default',
                title: 'Yandex Wiki',
                description:
                    'Бесплатная база знаний для сотрудников компании. \n\nСоздавайте вики-страницы, наполняйте их информацией \nи редактируйте вместе с коллегами.',
                buttons: [
                    {
                        text: 'Войти в Wiki',
                        primary: true,
                        theme: 'accent',
                        size: 'promo',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_IN_GOAL],
                    },
                    {
                        text: 'Зарегистрироваться',
                        primary: true,
                        theme: 'pseudo',
                        size: 'promo',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_UP_GOAL],
                    },
                ],
            },
            {
                type: 'extended-features-block',
                items: [
                    {
                        title: 'Создайте базу знаний',
                        text: 'Храните на Wiki описания проектов, рабочие инструкции и другую информацию. Структурируйте информацию: создавайте иерархию вложенных страниц и связывайте их перекрёстными ссылками.',
                    },
                    {
                        title: 'Экономьте время на поиске информации',
                        text: 'Используйте поиск по всем страницам базы знаний, чтобы быстро находить ответы на ваши вопросы.',
                    },
                    {
                        title: 'Оформляйте страницы',
                        text: 'Форматируйте текст, добавляйте рисунки, таблицы и ссылки с помощью разметки Markdown. Помогайте коллегам фокусироваться на главном с помощью заголовков и катов.',
                    },
                    {
                        title: 'Управляйте доступом',
                        text: 'Откройте базу знаний для всех сотрудников, ограничьте возможность редактировать ваши страницы или запретите просматривать конфиденциальную информацию.',
                    },
                    {
                        title: 'Отслеживайте историю изменений',
                        text: 'Все правки страниц сохраняются в истории изменений. Вы получите уведомления об изменениях страницы, на которую подписаны.',
                    },
                    {
                        title: 'Добавляйте задачи и формы',
                        text: 'Добавляйте списки задач из Tracker — названия и исполнители задач будут обновляться автоматически. Встраивайте на страницы формы для заявок и опросов из Forms.',
                        link: {
                            theme: 'normal',
                            arrow: true,
                            text: 'Yandex Tracker',
                            url: 'https://cloud.yandex.ru/services/tracker',
                        },
                    },
                ],
            },
            {
                type: 'media-block',
                direction: 'media-content',
                title: 'Ведите план работы по проекту на Wiki',
                description:
                    'Храните на Wiki полное описание проекта и добавляйте списки задач из Tracker.',
                media: {
                    image: {
                        src: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/wiki/wiki-interface-1.png',
                        disableCompress: true,
                    },
                },
                largeMedia: true,
            },
            {
                type: 'slider-block',
                anchor: {
                    url: 'solutions',
                    text: 'Решения',
                },
                title: {
                    text: 'Какие ещё задачи решает Wiki',
                },
                children: [
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_18_2021.png',
                            title: 'Внутренняя база знаний',
                        },
                        text: 'Создайте общую базу знаний для всей компании и наполняйте её вместе с коллегами.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_1.png',
                            title: 'Проектная документация',
                        },
                        text: 'Храните на Wiki полное описание проекта и добавляйте списки задач из Tracker.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_14.png',
                            title: 'Корпоративный портал',
                        },
                        text: 'Сделайте удобный корпоративный портал для сотрудников. Разместите всю важную информацию, ссылки, контакты, формы заявок.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_5.png',
                            title: 'Инструкции по процессам',
                        },
                        text: 'Храните на Wiki инструкции по любым процессам вашей компании. Встраивайте формы из Forms, чтобы создавать задачи в Tracker прямо с вики-страницы.',
                    },
                    {
                        type: 'card',
                        header: {
                            image: 'https://storage.yandexcloud.net/cloud-www-assets/constructor/services/solution_17_2021.png',
                            title: 'Ведите личные заметки',
                        },
                        text: 'Записывайте на Вики черновики, планы и результаты встреч.',
                    },
                ],
            },
            {
                type: 'questions-block',
                title: 'Вопросы и ответы',
                anchor: {
                    text: 'Вопросы и ответы',
                    url: 'questions',
                },
                items: [
                    {
                        title: 'Как воспользоваться Yandex Wiki?',
                        text: 'Yandex Wiki — это бесплатный сервис для создания базы знаний. Чтобы воспользоваться Wiki, достаточно войти в аккаунт:\n* Если вы сотрудник организации Yandex Cloud, войдите в ваш рабочий аккаунт. Сервис Wiki будет активирован для вашей организации, и вы сразу сможете создавать и редактировать страницы.\n* Если у вас ещё нет организации, войдите в аккаунт на Яндексе. Для вас автоматически будет создана новая организация, и вы сможете использовать Wiki.',
                    },
                    {
                        title: 'Как настроить интеграцию Tracker и Wiki?',
                        text: 'Достаточно подключить Tracker в вашей организации. Вы сможете добавлять на страницы и в динамические таблицы ссылки на задачи и видеть их названия, а также открывать карточку задачи из интерфейса Wiki.',
                        link: {
                            url: 'https://cloud.yandex.ru/services/tracker',
                            text: 'На страницу Tracker',
                            theme: 'normal',
                            arrow: true,
                        },
                    },
                    {
                        title: 'Как настроить интеграцию Wiki и Forms?',
                        text: 'Чтобы встроить форму на страницу, используйте код для вставки на Wiki. \n\nЧтобы сохранять на странице ответы на форму, в интерфейсе Yandex Forms перейдите на вкладку Интеграции и выберите Yandex Wiki. Подробная инструкция по ссылке.',
                        link: {
                            url: 'https://cloud.yandex.ru/services/forms',
                            text: 'На страницу Yandex Forms',
                            theme: 'normal',
                            arrow: true,
                        },
                    },
                ],
            },
            {
                type: 'info-block',
                title: 'Начните пользоваться Yandex Wiki',
                buttons: [
                    {
                        text: 'Войти в Wiki',
                        theme: 'raised',
                        url: WIKI_ROOT_URL,
                        metrikaGoals: [SIGN_IN_GOAL],
                    },
                ],
                sectionsTitle: 'Полезные ссылки',
                links: [
                    {
                        text: 'Документация',
                        url: 'https://cloud.yandex.ru/docs/wiki',
                    },
                    {
                        text: 'Тарифы',
                        url: 'https://cloud.yandex.ru/docs/wiki/pricing',
                    },
                ],
            },
        ],
    } as PageContent;
};

export const getPromoPageConfig = (lang: Lang, theme: Theme) => {
    return lang === 'ru' ? getRuPromoPageConfig(theme) : getEnPromoPageConfig(theme);
};
